import * as alumniPage from '@/app/l/[region]/[language]/alumni/[[...pagination]]/alumni.urls';
import * as applyFormPage from '@/app/l/[region]/[language]/apply/[...extraParams]/apply.urls';
import * as apprenticeshipPage from '@/app/l/[region]/[language]/apprenticeship/[course]/apprenticeship.urls';
import * as b2bPage from '@/app/l/[region]/[language]/b2b/b2b.urls';
import * as b2bApplyPage from '@/app/l/[region]/[language]/b2b/apply/b2b-apply.urls';
import * as blogHomepage from '@/app/l/[region]/[language]/blog/[[...pagination]]/blog.urls';
import * as blogArticlePage from '@/app/l/[region]/[language]/blog-article/[slug]/blog-article.urls';
import * as blogAuthorPage from '@/app/l/[region]/[language]/blog-author/[author]/[[...pagination]]/blog-author.urls';
import * as campaignFormPage from '@/app/l/[region]/[language]/campaign-form/campaign-form.urls';
import * as campusPage from '@/app/l/[region]/[language]/campus/[campus]/[[...extraParams]]/campus.urls';
import * as careerPage from '@/app/l/[region]/[language]/career/career.urls';
import * as contactFormPage from '@/app/l/[region]/[language]/contact/contact.urls';
import * as coursePage from '@/app/l/[region]/[language]/course/[course]/course.urls';
import * as faqPage from '@/app/l/[region]/[language]/faq/faq.urls';
import * as financingPage from '@/app/l/[region]/[language]/financing/financing.urls';
import * as financingFormPage from '@/app/l/[region]/[language]/financing-form/financing-form.urls';
import * as fullApplyPage from '@/app/l/[region]/[language]/full-apply/full-apply.urls';
import * as homepage from '@/app/l/[region]/[language]/home/[[...extraParams]]/home.urls';
import * as iframeEmbed from '@/app/l/[region]/[language]/iframe-embed/[...urlSegments]/iframe-embed.urls';
import * as pdp from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/pdp.urls';
import * as privacyPage from '@/app/l/[region]/[language]/privacy/privacy.urls';
import * as quizPage from '@/app/l/[region]/[language]/quiz/quiz.urls';
import * as quizMatchPage from '@/app/l/[region]/[language]/quiz-match/[course]/quiz-match.urls';
import * as syllabusFormPage from '@/app/l/[region]/[language]/syllabus/[course]/syllabus.urls';
import * as termsPage from '@/app/l/[region]/[language]/terms/terms.urls';
import * as thankYouPage from '@/app/l/[region]/[language]/thank-you/thank-you.urls';
import * as thankYouDynamicPage from '@/app/l/[region]/[language]/thank-you-dynamic/thank-you-dynamic.urls';
import * as deRegion from '@/app/urls/de';
import * as deEnRegion from '@/app/urls/de-en';
import * as esRegion from '@/app/urls/es';
import * as esEnRegion from '@/app/urls/es-en';
import * as frRegion from '@/app/urls/fr';
import * as frEnRegion from '@/app/urls/fr-en';
import * as gbRegion from '@/app/urls/gb';
import * as nlRegion from '@/app/urls/nl';
import * as nlEnRegion from '@/app/urls/nl-en';
import * as ptRegion from '@/app/urls/pt';
import * as ptEnRegion from '@/app/urls/pt-en';
import * as usRegion from '@/app/urls/us';
import * as wwRegion from '@/app/urls/ww';
import config from '@/config';
import { isValidSalesforceId } from '@/lib/salesforce';
import { regionMainLanguage, toCamelCase } from '@/lib/utils';
import type { ApprenticeshipPages } from '@/app/l/[region]/[language]/apprenticeship/[course]/apprenticeship.urls';
import type { CampusPages } from '@/app/l/[region]/[language]/campus/[campus]/[[...extraParams]]/campus.urls';
import type { CoursePages } from '@/app/l/[region]/[language]/course/[course]/course.urls';
import type { IframePages } from '@/app/l/[region]/[language]/iframe-embed/[...urlSegments]/iframe-embed.urls';
import type { PdpPages } from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/pdp.urls';
import type { QuizMatchPages } from '@/app/l/[region]/[language]/quiz-match/[course]/quiz-match.urls';
import type { SyllabusPages } from '@/app/l/[region]/[language]/syllabus/[course]/syllabus.urls';
import type {
  AvailableCourseTrack,
  Campus,
  Language,
  Region,
  StandardishRegion,
  UrlEntry,
  UrlRegion,
} from '@/types';

export type BasePageParams<T = string[]> = {
  extraParams?: T;
  language: Language;
  region: Region;
};

export type PossibleParams = {
  author: string;
  campus: Campus;
  cohortId: string[];
  course: AvailableCourseTrack;
  pagination: string[];
  slug: string;
  timezone: string[];
  urlSegments: string[];
  userCountry: string;
};

export type FullParams = BasePageParams & Partial<PossibleParams>;

export type PageType =
  | 'alumni'
  | 'apply'
  | 'b2b'
  | 'b2bApply'
  | 'blog'
  | 'blogArticle'
  | 'blogAuthor'
  | 'campaignForm'
  | 'career'
  | 'contact'
  | 'faq'
  | 'financing'
  | 'financingForm'
  | 'fullApply'
  | 'home'
  | 'privacy'
  | 'quiz'
  | 'terms'
  | 'thankYou'
  | 'thankYouDynamic'
  | ApprenticeshipPages
  | CampusPages
  | CoursePages
  | IframePages
  | PdpPages
  | QuizMatchPages
  | SyllabusPages;

// SimplePageType needs to match the page's folder name for the sitemap to work
// Could be interesting to enforce that more directly in TypeScript
export type SimplePageType =
  | Exclude<
      PageType,
      | ApprenticeshipPages
      | CampusPages
      | CoursePages
      | IframePages
      | PdpPages
      | QuizMatchPages
      | SyllabusPages
    >
  | 'apprenticeship'
  | 'campus'
  | 'course'
  | 'iframeEmbed'
  | 'pdp'
  | 'quizMatch'
  | 'syllabus';

type UrlConfig = { isIndexable?: boolean };

export const internalToRouteMapping: Record<
  PageType,
  Partial<Record<UrlRegion, UrlEntry>>
> = {
  ...alumniPage.urls,
  ...apprenticeshipPage.urls,
  ...applyFormPage.urls,
  ...b2bApplyPage.urls,
  ...b2bPage.urls,
  ...blogArticlePage.urls,
  ...blogAuthorPage.urls,
  ...blogHomepage.urls,
  ...campaignFormPage.urls,
  ...campusPage.urls,
  ...careerPage.urls,
  ...contactFormPage.urls,
  ...coursePage.urls,
  ...faqPage.urls,
  ...financingFormPage.urls,
  ...financingPage.urls,
  ...fullApplyPage.urls,
  ...homepage.urls,
  ...iframeEmbed.urls,
  ...pdp.urls,
  ...privacyPage.urls,
  ...quizPage.urls,
  ...quizMatchPage.urls,
  ...syllabusFormPage.urls,
  ...termsPage.urls,
  ...thankYouPage.urls,
  ...thankYouDynamicPage.urls,
};

export const normalizeUrlEntry = (entry: UrlEntry): Exclude<UrlEntry, string> =>
  typeof entry === 'string' ? { url: entry, isIndexable: true } : entry;

export const getUrlRegion = (params: BasePageParams): UrlRegion => {
  const { language, region } = params;
  const mainLanguage = regionMainLanguage[region];
  const standardishRegion = region === 'uk' ? 'gb' : region;

  return language === mainLanguage
    ? standardishRegion
    : (`${standardishRegion}-${language}` as UrlRegion);
};

export const splitUrlRegion = (urlRegion: UrlRegion): BasePageParams => {
  const [tempRegion, tempLanguage] = urlRegion.split('-') as [
    StandardishRegion,
    Language?
  ];
  const region = tempRegion === 'gb' ? 'uk' : tempRegion;
  const language = tempLanguage ?? regionMainLanguage[region];

  return { language, region };
};

export const getPageUrl = (
  page: PageType,
  params: FullParams,
  options?: { withOrigin: boolean }
): string | undefined => {
  const { withOrigin } = { withOrigin: false, ...options };

  const urlRegion = getUrlRegion(params);
  const urlEntry = internalToRouteMapping[page][urlRegion];
  if (typeof urlEntry === 'undefined') return;

  let pathname = `/${urlRegion}`;

  const localizedPart = normalizeUrlEntry(urlEntry).url;
  if (localizedPart) pathname += `/${localizedPart}`;

  if (page === 'apply' && params.cohortId) {
    const cohortId = params.cohortId.find(isValidSalesforceId);
    if (cohortId) pathname += `/c/${cohortId}`;
  }

  if (page === 'blogArticle' && params.slug) {
    pathname += `/${params.slug}`;
  }

  if (page === 'blogAuthor' && params.author) {
    pathname += `/${params.author}`;
  }

  if (page.startsWith('iframeEmbed') && params.urlSegments) {
    const joinedSegments = params.urlSegments.slice(1).join('/');
    pathname += `/${joinedSegments}`;
  }

  if (params.pagination) {
    const pageNumber = params.pagination.find((part) =>
      Number.isInteger(Number(part))
    );
    if (pageNumber) pathname += `/page/${pageNumber}`;
  }
  const origin = withOrigin ? config.baseUrl : '';

  return `${origin}${pathname}`;
};

export const urlToRouteMapping: Record<
  UrlRegion,
  Record<
    string,
    `/l/${Region}/${Language}` | `/l/${Region}/${Language}/${string}`
  >
> = {
  ...deRegion.urls,
  ...deEnRegion.urls,
  ...esRegion.urls,
  ...esEnRegion.urls,
  ...frRegion.urls,
  ...frEnRegion.urls,
  ...gbRegion.urls,
  ...nlRegion.urls,
  ...nlEnRegion.urls,
  ...ptRegion.urls,
  ...ptEnRegion.urls,
  ...usRegion.urls,
  ...wwRegion.urls,
};

const pageTypeConfig = {
  alumni: alumniPage,
  apply: applyFormPage,
  apprenticeship: apprenticeshipPage,
  b2b: b2bPage,
  b2bApply: b2bApplyPage,
  blog: blogHomepage,
  blogArticle: blogArticlePage,
  blogAuthor: blogAuthorPage,
  campaignForm: campaignFormPage,
  campus: campusPage,
  career: careerPage,
  contact: contactFormPage,
  course: coursePage,
  faq: faqPage,
  financing: financingPage,
  financingForm: financingFormPage,
  fullApply: fullApplyPage,
  home: homepage,
  iframeEmbed,
  pdp,
  privacy: privacyPage,
  quiz: quizPage,
  quizMatch: quizMatchPage,
  syllabus: syllabusFormPage,
  terms: termsPage,
  thankYou: thankYouPage,
  thankYouDynamic: thankYouDynamicPage,
} as Record<SimplePageType, UrlConfig>;

const regionConfig = {
  de: deRegion,
  'de-en': deEnRegion,
  es: esRegion,
  'es-en': esEnRegion,
  fr: frRegion,
  'fr-en': frEnRegion,
  gb: gbRegion,
  nl: nlRegion,
  'nl-en': nlEnRegion,
  pt: ptRegion,
  'pt-en': ptEnRegion,
  us: usRegion,
  ww: wwRegion,
} as Record<UrlRegion, UrlConfig>;

export const normalizeUrlConfig = (
  urlConfig: UrlConfig
): Required<UrlConfig> => ({ isIndexable: true, ...urlConfig });

const internalUrlToPage: Record<string, PageType> = {
  'b2b/apply': 'b2bApply',
};
export const getPageFromInternalRoute = (internalRoute: string) => {
  // The first 9 characters of the internal route correspond to /l/xx/yy/
  const page = internalRoute.slice(9) as PageType;
  return internalUrlToPage[page] ?? toCamelCase(page);
};

export const isPageIndexable = (
  page: PageType,
  params: FullParams
): boolean => {
  const urlRegion = getUrlRegion(params);
  const urlEntry = internalToRouteMapping[page][urlRegion];
  if (typeof urlEntry === 'undefined') return false;

  const simplePage = page.split('/')[0] as SimplePageType;
  const entryLevel = normalizeUrlEntry(urlEntry);
  const pageTypeLevel = normalizeUrlConfig(pageTypeConfig[simplePage]);
  const regionLevel = normalizeUrlConfig(regionConfig[urlRegion]);

  return (
    Boolean(entryLevel.isIndexable) &&
    pageTypeLevel.isIndexable &&
    regionLevel.isIndexable
  );
};
